import React, { useRef } from "react";
import { styled } from "styled-components";
import profileCamera from "@src/assets/profileCamera.svg";
import { transferToNative, getDeviceType } from "@src/lib/WebAppBridge";

export type ProfileUploadProps = {
    imageUrl?: string | null; // 현재 표시할 이미지 (없으면 placeholder 표시)
    placeholderSrc: string; // 기본 이미지
    readOnly?: boolean; // 읽기 전용 모드 여부
    isUploading?: boolean; // 업로드 중 상태
    accept?: string; // 업로드 허용 파일 타입 (기본: image/*)

    onFileSelected?: (file: File) => void; // 파일 선택 시 부모에 알림

    addLabel?: string; // 이미지 없을 때 버튼 레이블 (기본: 추가)
    changeLabel?: string; // 이미지 있을 때 버튼 레이블 (기본: 변경하기)
    showCameraIcon?: boolean; // 추가: 아이콘만 표시할지 여부
};

const ProfileUpload: React.FC<ProfileUploadProps> = ({
    imageUrl,
    placeholderSrc,
    readOnly = false,
    isUploading = false,
    accept = "image/*",
    onFileSelected,
    addLabel = "추가",
    changeLabel = "변경하기",
    showCameraIcon = false,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const hasImage = !!imageUrl;
    const canEdit = !readOnly && !isUploading;

    const openFilePicker = () => {
        if (!canEdit) return;
        if (getDeviceType() === "android") {
            transferToNative("", "onClickProfile");
        } else if (!canEdit) return;
        inputRef.current?.click();
    };

    const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const file = e.target.files?.[0];
        if (!file || !onFileSelected || !canEdit) return;
        onFileSelected(file);

        // 같은 파일 다시 선택할 수 있도록 input 값 초기화
        if (inputRef.current) inputRef.current.value = "";
    };

    const buttonLabel = hasImage ? changeLabel : addLabel;

    return (
        <Wrap aria-busy={isUploading}>
            <Preview
                role="img"
                aria-label={hasImage ? "업로드된 프로필 이미지" : "기본 프로필 이미지"}
                style={{
                    backgroundImage: `url(${hasImage ? imageUrl : placeholderSrc})`,
                }}
            />
            {showCameraIcon && canEdit && (
                <CameraIconWrap onClick={openFilePicker}>
                    <img src={profileCamera} alt="프로필 변경" />
                </CameraIconWrap>
            )}
            {!showCameraIcon && !readOnly && (
                <AddBtn type="button" onClick={openFilePicker} disabled={!canEdit}>
                    {buttonLabel}
                </AddBtn>
            )}
            <HiddenFile ref={inputRef} type="file" accept={accept} onChange={handleFileChange} aria-hidden tabIndex={-1} />
        </Wrap>
    );
};

export default ProfileUpload;

const Wrap = styled.div`
    position: relative;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
`;
const Preview = styled.div`
    width: 100px;
    height: 100px;
    border-radius: 9999px;
    background: #f0f0f0 center/cover no-repeat;
`;
const AddBtn = styled.button`
    padding: 0 8px;
    border-radius: 4px;
    font-size: 14px;
    line-height: 140%;
    background: #ffffff;
    color: #2f6bff;
    border: 1px solid #2f6bff;

    &:hover {
        filter: brightness(0.98);
    }
`;
const HiddenFile = styled.input`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    border: 0;
`;

const Assistive = styled.div`
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
`;

const CameraIconWrap = styled.div`
    display: none;
`;
